import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import useMediaQuery from '@mui/material/useMediaQuery'

import SVGWrapper from '../SVGWrapper'
import { IconEdit } from '../../assets/icons'
import { MOBILE_VIEWPORT } from '../../guided-toolbox/utils/breakpoints'
import {
  FONT_FAMILY,
  BOX_PAIR_MAX_WIDTH,
  BOX_GAP,
  GUTTER_LABEL_MAX_WIDTH,
  GUTTER_GAP,
  Chevron,
} from './shared'
import { cardBorderColor, ArrowGlyph } from '../advice/shared'

/* ---------------------------------------------------------------- desktop -- */

const DesktopWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const DesktopTitle = styled.div`
  font-family: ${FONT_FAMILY};
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.darkestGray};
  margin-bottom: 16px;
  text-align: center;
`

// Equal side gutters keep the box pair centered, even when only the left gutter
// has a label.
const DesktopGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr minmax(0, ${BOX_PAIR_MAX_WIDTH}px) 1fr;
  align-items: center;
  column-gap: ${GUTTER_GAP}px;
  width: 100%;
`

const GutterLabel = styled.div`
  max-width: ${GUTTER_LABEL_MAX_WIDTH}px;
  justify-self: end;
  font-family: ${FONT_FAMILY};
  font-size: 13px;
  font-weight: 400;
  color: ${p => p.theme.darkestGray};
  text-align: right;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const BoxPair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: ${BOX_GAP}px;
  width: 100%;
`

const boxBase = `
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  padding: 10px 20px;
  font-family: ${FONT_FAMILY};
  font-size: 15px;
  text-align: center;
`

const CurrentBox = styled.div`
  ${boxBase};
  background: ${p => p.theme.addButtonHoverColor};
  color: ${p => p.theme.mediumGray};
`

const ModifiedBox = styled.button`
  ${boxBase};
  position: relative;
  width: 100%;
  border: none;
  cursor: ${p => (p.editable ? 'pointer' : 'default')};
  background: ${p => p.theme.ternaryColor};
  color: ${p => p.theme.darkestGray};
`

const DesktopChevron = styled(Chevron)`
  position: absolute;
  right: 16px;
  color: ${p => p.theme.primaryColor};
`

// Keep multi-part values in one flex item so spaces between their inline pieces
// are preserved.
const Value = styled.span`
  display: inline;
`

const DesktopRow = ({ label, current, modified, onEdit, editAriaLabel, labelPlacement, theme }) => {
  const editable = !!onEdit
  const boxPair = (
    <BoxPair>
      <CurrentBox theme={theme}>
        <Value>{current}</Value>
      </CurrentBox>
      <ModifiedBox
        as={editable ? 'button' : 'div'}
        theme={theme}
        editable={editable}
        {...(editable ? { 'type': 'button', 'onClick': onEdit, 'aria-label': editAriaLabel } : {})}>
        <Value>{modified}</Value>
        {editable && <DesktopChevron theme={theme} />}
      </ModifiedBox>
    </BoxPair>
  )

  if (labelPlacement === 'gutter') {
    return (
      <DesktopGrid>
        <GutterLabel theme={theme}>{label}</GutterLabel>
        {boxPair}
        <div />
      </DesktopGrid>
    )
  }

  return (
    <DesktopWrap>
      <DesktopTitle theme={theme}>{label}</DesktopTitle>
      <DesktopGrid>
        <div />
        {boxPair}
        <div />
      </DesktopGrid>
    </DesktopWrap>
  )
}

/* ----------------------------------------------------------------- mobile -- */

const Card = styled.div`
  position: relative;
  border: 2px solid ${cardBorderColor};
  border-radius: 14px;
  padding: 18px 20px 22px;
  background: #ffffff;
`

// Leave room for the overlaid edit button so long labels do not run beneath it.
const CardLabel = styled.div`
  font-family: ${FONT_FAMILY};
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.darkestGray};
  text-align: center;
  padding: 0 28px;
`

const EditButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border: none;
  background: none;
  cursor: pointer;
  color: ${p => p.theme.mediumGray};
`

const CardBody = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  column-gap: 12px;
`

const CurrentValue = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: ${FONT_FAMILY};
  font-size: 15px;
  color: ${p => p.theme.mediumGray};
  text-align: center;
`

const MobileArrow = styled(ArrowGlyph)`
  flex: 0 0 auto;
  color: ${p => p.theme.primaryColor};
`

const MobileModifiedValue = styled.button`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: none;
  cursor: ${p => (p.editable ? 'pointer' : 'default')};
  color: ${p => p.theme.primaryColor};
  font-family: ${FONT_FAMILY};
  font-size: 15px;
  font-weight: 600;
  text-align: center;
`

const MobileRow = ({ label, current, modified, onEdit, editAriaLabel, theme }) => {
  const editable = !!onEdit

  return (
    <Card theme={theme}>
      <CardLabel theme={theme}>{label}</CardLabel>

      {editable && (
        <EditButton type='button' theme={theme} onClick={onEdit} aria-label={editAriaLabel}>
          <SVGWrapper svg={IconEdit} size='mediumLarge' />
        </EditButton>
      )}

      <CardBody>
        <CurrentValue theme={theme}>{current}</CurrentValue>
        <MobileArrow theme={theme} />
        <MobileModifiedValue
          as={editable ? 'button' : 'div'}
          theme={theme}
          editable={editable}
          {...(editable
            ? { 'type': 'button', 'onClick': onEdit, 'aria-label': editAriaLabel }
            : {})}>
          {modified}
        </MobileModifiedValue>
      </CardBody>
    </Card>
  )
}

/**
 * Displays current and modified values in the desktop/mobile summary layout.
 * Omit `onEdit` to render the row without edit controls.
 */
const AdviceSummaryRow = ({
  label,
  current,
  modified,
  onEdit,
  editAriaLabel,
  labelPlacement = 'title',
  className,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)
  const ariaLabel = editAriaLabel ?? `Edit ${label}`
  const Variant = isMobile ? MobileRow : DesktopRow

  return (
    <div className={className}>
      <Variant
        label={label}
        current={current}
        modified={modified}
        onEdit={onEdit}
        editAriaLabel={ariaLabel}
        labelPlacement={labelPlacement}
        theme={theme}
      />
    </div>
  )
}

AdviceSummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  current: PropTypes.node.isRequired,
  modified: PropTypes.node.isRequired,
  /** Makes the modified value editable; omit to render a read-only row. */
  onEdit: PropTypes.func,
  /** Accessible edit label; defaults to `Edit {label}`. */
  editAriaLabel: PropTypes.string,
  /** Desktop only: places the label above or beside the value boxes. */
  labelPlacement: PropTypes.oneOf(['title', 'gutter']),
  className: PropTypes.string,
}

export default AdviceSummaryRow
